import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LANGS, LANDING_PAGES, LEGAL_PAGES, PROJECTS_SEGMENT, homePath, landingPath, casePath, legalPath } from '../seo/seoConfig';

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
    const [, currentLang, slug, caseSlug] = pathname.split('/');
    if (LANGS.includes(currentLang)) {
      const landingKey = Object.keys(LANDING_PAGES).find((k) => LANDING_PAGES[k].slugs[currentLang] === slug);
      if (landingKey) return navigate(landingPath(langCode, landingKey));
      const legalKey = Object.keys(LEGAL_PAGES).find((k) => LEGAL_PAGES[k].slugs[currentLang] === slug);
      if (legalKey) return navigate(legalPath(langCode, legalKey));
      if (slug === PROJECTS_SEGMENT[currentLang] && caseSlug) return navigate(casePath(langCode, caseSlug));
    }
    navigate(`${homePath(langCode)}${hash}`);
  };

  return (
    <div className="relative">
      <button
        aria-label="Language"
        data-testid="language-selector-btn"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <Globe className="w-5 h-5" />
        <span className="text-2xl">{currentLanguage.flag}</span>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
            {languages.map((lang) => (
              <button
                key={lang.code}
                data-testid={`language-option-${lang.code}`}
                onClick={() => changeLanguage(lang.code)}
                className={`w-full flex items-center justify-between px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  i18n.language === lang.code ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{lang.flag}</span>
                  <span className={`font-medium ${
                    i18n.language === lang.code ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {lang.name}
                  </span>
                </div>
                {i18n.language === lang.code && (
                  <Check className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSelector;
